import type { Log } from '@sentry/react';

/**
 * Enrich console-forwarded Sentry logs with their originating stack.
 *
 * `consoleLoggingIntegration` forwards `console.warn` / `console.error` as logs
 * whose only attribute is `sentry.origin: "auto.log.console"` — no stack — so a
 * generic warning like Leaflet's `wrong listener type: undefined` is
 * indistinguishable from one raised anywhere else, and can't be filtered
 * specifically in Sentry. The whole `console.warn → integration → captureLog →
 * beforeSendLog` chain runs synchronously, so a stack captured inside the
 * `beforeSendLog` hook still contains the original caller's frames.
 *
 * `attachConsoleOrigin` is that hook: it adds the caller stack as a
 * `console.stack` attribute (+ first frame as `console.caller`) so Sentry-side
 * rules can target a specific source (e.g. leaflet-geoman) instead of the bare
 * message. Readable in dev; in a minified prod bundle the frames are minified
 * (SDK frames can't be identified to drop), but the stack is still attached.
 */

// Frames belonging to the SDK's console→log forwarding chain (and this hook).
// Identifiable only where node_modules paths survive (dev); in a minified prod
// bundle none match, so the full stack is kept rather than mis-trimmed.
const SDK_FRAME_MARKERS = [
  '/@sentry/',
  'consoleSandbox',
  '_INTERNAL_captureLog',
  'triggerHandlers',
  'instrument/console',
  'console-log-origin.ts',
  'attachConsoleOrigin',
];

// In a minified prod bundle the SDK console→log chain (plus this hook) can't be
// dropped and occupies the first ~8 frames, so the cap must leave room for the
// real caller behind it.
const MAX_FRAMES = 20;
const MAX_LENGTH = 4096;

/**
 * Reduce a raw `Error.stack` to the caller frames worth logging: drop the
 * leading `Error` header and any SDK/self frames, then bound the result.
 * Returns `null` when nothing usable remains.
 */
export function deriveCallerStack(rawStack: string | undefined): string | null {
  if (!rawStack) return null;
  const frames = rawStack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .filter((line) => line !== 'Error' && !line.startsWith('Error:'))
    .filter((line) => !SDK_FRAME_MARKERS.some((marker) => line.includes(marker)));
  if (frames.length === 0) return null;
  return frames.slice(0, MAX_FRAMES).join('\n').slice(0, MAX_LENGTH);
}

/**
 * `beforeSendLog` hook. For console-forwarded logs, attach the originating
 * stack; everything else passes through untouched.
 */
export function attachConsoleOrigin(
  log: Log,
  getStack: () => string | undefined = () => new Error().stack,
): Log {
  if (log.attributes?.['sentry.origin'] !== 'auto.log.console') return log;
  const callerStack = deriveCallerStack(getStack());
  if (callerStack === null) return log;
  return {
    ...log,
    attributes: {
      ...log.attributes,
      'console.stack': callerStack,
      // A non-null callerStack is non-empty, so splitting yields a first line.
      'console.caller': callerStack.split('\n')[0]!,
    },
  };
}
